import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import { useGetData } from "@/hooks/useGetData";
import type { PhoneNumber } from "@/shared/components/PhoneNumberForm/types";
import type {
	BillingPreviewResponse,
	StripeCheckoutRequestData,
	StripeUpdateData,
} from "@/shared/components/PricingTable/types";
import { UserService } from "./UserService";

export class UseData {
	private constructor(private service: UserService) {}

	private static instance: UseData;

	static create() {
		if (!UseData.instance) {
			UseData.instance = new UseData(UserService.create());
		}
		return UseData.instance;
	}

	// Methods
	getData = async (): Promise<Partial<UserProfile & UserProfileRead>> => {
		return await this.service.getData();
	};

	updateData = async (data: Partial<UserProfile>) => {
		await this.service.updateData(data);
	};

	savePhone = async (data: PhoneNumber) => {
		await this.service.savePhone(data);
	};

	validatePhone = async (data: PhoneNumber) => {
		await this.service.validatePhone(data);
	};

	getStripeBillingUrl = async (returnUrl: string): Promise<string> => {
		return await this.service.getStripeBillingUrl(returnUrl);
	};

	getStripeCheckoutUrl = async (params: StripeCheckoutRequestData) => {
		return await this.service.getStripeCheckoutUrl(params);
	};

	updateStripeSubscription = async (data: StripeUpdateData): Promise<void> => {
		return await this.service.updateStripeSubscription(data);
	};

	deleteStripeSubscription = async () => {
		return await this.service.deleteStripeSubscription();
	};

	getStripeBillingPreview = async (
		data: StripeUpdateData,
	): Promise<BillingPreviewResponse> => {
		return await this.service.getStripeBillingPreview(data);
	};

	getTokenForChat = async (): Promise<string> => {
		return await this.service.getTokenForChat();
	};

	// Hooks

	useGetData = () =>
		useGetData<Partial<UserProfile & UserProfileRead>, void>(this.getData);

	useUpdateData = () => useGetData<void, Partial<UserProfile>>(this.updateData);

	useSavePhone = () => useGetData<void, PhoneNumber>(this.savePhone);

	useValidatePhone = () => useGetData<void, PhoneNumber>(this.validatePhone);

	useGetStripeBillingUrl = () =>
		useGetData<string, string>(this.getStripeBillingUrl);

	useGetStripeCheckout = () =>
		useGetData<string, StripeCheckoutRequestData>(this.getStripeCheckoutUrl);

	useGetStripeUpdateSubscription = () =>
		useGetData<void, StripeUpdateData>(this.updateStripeSubscription);

	useDeleteStripeSubscription = () =>
		useGetData<void, void>(this.deleteStripeSubscription);

	useGetStripeBillingPreview = () =>
		useGetData<BillingPreviewResponse, StripeUpdateData>(
			this.getStripeBillingPreview,
		);

	useGetTokenForChat = () => useGetData<string, void>(this.getTokenForChat);
}
